import { queryOptions } from "@tanstack/react-query";
import { apiFetch } from "@/shared/api";

export type RoutineKind = "interval" | "daily" | "weekdays" | "weekly" | "webhook";

// Discriminated union, mirrors backend.routines.validate_schedule. Stored
// in `schedule_spec` as plain JSON; backend recomputes `next_run_at` on
// every schedule change.
export type ScheduleSpec =
  | { kind: "interval"; minutes: number }
  | { kind: "daily"; time: string; tz?: string }
  | { kind: "weekdays"; time: string; tz?: string }
  | { kind: "weekly"; weekday: number; time: string; tz?: string }
  | { kind: "webhook" };

export interface Routine {
  id: string;
  space_id: string;
  owner_user_id: string;
  name: string;
  prompt: string;
  agent_ref: string;
  extra_connector_ids: string[];
  schedule_kind: RoutineKind;
  // Backend stores spec without the `kind` discriminator (it lives in
  // `schedule_kind`). The frontend reconstructs the union via the helper
  // `routineSchedule(routine)`.
  schedule_spec: Record<string, unknown>;
  webhook_token: string | null;
  // Public webhook contract — relevant only when schedule_kind="webhook".
  // See EXTERNAL_API.md §3.
  prompt_template: string | null;
  expects_files: boolean;
  description: string;
  enabled: boolean;
  next_run_at: string | null;
  last_run_at: string | null;
  last_status: "ok" | "failed" | null;
  last_error: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface RoutineRun {
  session_id: string;
  title: string | null;
  status: string;
  created_at: string | null;
  updated_at: string | null;
}

export interface RoutineInput {
  name: string;
  prompt: string;
  agent_ref: string;
  extra_connector_ids: string[];
  schedule_kind: RoutineKind;
  schedule_spec: Record<string, unknown>;
  enabled: boolean;
  /** Target tenancy. `null` = personal scope; `"<uuid>"` = pinned to that
   *  team space. Same shape as agents/connectors. */
  space_id?: string | null;
  prompt_template?: string | null;
  expects_files?: boolean;
  description?: string;
}

export interface RoutinePatch {
  name?: string;
  prompt?: string;
  agent_ref?: string;
  extra_connector_ids?: string[];
  schedule_kind?: RoutineKind;
  schedule_spec?: Record<string, unknown>;
  enabled?: boolean;
  prompt_template?: string | null;
  expects_files?: boolean;
  description?: string;
}

type Scope = "personal" | "space";

/**
 * Routines visible in a given scope.
 * - `space` (default) — the currently-selected space (`X-Space-Id` header).
 * - `personal` — the user's personal space, regardless of selection. Used
 *    by the sidebar /routines page where the user expects only their own.
 */
export const routinesQueryOptions = (scope: Scope = "space") =>
  queryOptions({
    queryKey: ["routines", scope],
    queryFn: async (): Promise<Routine[]> => {
      const resp = await apiFetch(`/api/routines?scope=${scope}`);
      const data: { routines: Routine[] } = await resp.json();
      return data.routines ?? [];
    },
    staleTime: 30_000,
  });

export const routineDetailQueryOptions = (id: string | null) =>
  queryOptions({
    queryKey: ["routines", id],
    queryFn: async (): Promise<Routine> => {
      const resp = await apiFetch(`/api/routines/${id}`);
      if (!resp.ok) throw new Error(await resp.text());
      return resp.json();
    },
    enabled: !!id,
    staleTime: 30_000,
  });

export const routineRunsQueryOptions = (id: string | null) =>
  queryOptions({
    queryKey: ["routines", id, "runs"],
    queryFn: async (): Promise<RoutineRun[]> => {
      const resp = await apiFetch(`/api/routines/${id}/runs`);
      if (!resp.ok) throw new Error(await resp.text());
      const data: { runs: RoutineRun[] } = await resp.json();
      return data.runs ?? [];
    },
    enabled: !!id,
    staleTime: 10_000,
  });

export async function createRoutine(input: RoutineInput): Promise<Routine> {
  const resp = await apiFetch("/api/routines", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!resp.ok) throw new Error(await resp.text());
  return resp.json();
}

export async function updateRoutine(id: string, patch: RoutinePatch): Promise<Routine> {
  const resp = await apiFetch(`/api/routines/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!resp.ok) throw new Error(await resp.text());
  return resp.json();
}

export async function deleteRoutine(id: string): Promise<void> {
  const resp = await apiFetch(`/api/routines/${id}`, { method: "DELETE" });
  if (!resp.ok) throw new Error(await resp.text());
}

export async function runRoutineNow(id: string): Promise<{ session_id: string }> {
  const resp = await apiFetch(`/api/routines/${id}/run`, { method: "POST" });
  if (!resp.ok) throw new Error(await resp.text());
  return resp.json();
}
